import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type Dimensions = {
  width: number;
  height: number;
  padding: number;
};

type SvgPathElement = {
  id: string;
  text: string;
  svgPath: string;
};

export const CurveText: React.FC<{
  text: string;
  radius?: number;
  className?: string;
  id: string | number;
}> = ({ id, text, radius = 100, className = '' }) => {
  const originalTextRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<SVGTextElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [dimensions, setDimensions] = useState<Dimensions>({ width: 400, height: 300, padding: 0 });
  const [svgPathElements, setSvgPathElements] = useState<SvgPathElement[]>([]);
  const [textBreaks, setTextBreaks] = useState<{ [key: string]: string }>({});

  const updateTextBreaks = useCallback(() => {
    if (originalTextRef.current) {
      const el = originalTextRef.current;
      const words = el.innerText.trim().split(/\s+/);
      const tempElements: { id: string; span: HTMLSpanElement }[] = [];

      el.innerHTML = '';
      words.map((word, index) => {
        const span = document.createElement('span');
        span.textContent = word + ' ';
        el.appendChild(span);
        tempElements.push({ id: `span${id}${index}`, span: span });
      });

      var prevTop: number | null = null;
      var currentSpanId: string | undefined = undefined;
      const texts = tempElements.reduce((memo, item) => {
        const span = item.span;
        const top = span.getBoundingClientRect().top;

        if (span.textContent && ((prevTop !== null && top !== prevTop) || prevTop === null)) {
          currentSpanId = item.id;
          memo[item.id] = span.textContent;
        } else if (currentSpanId && span.textContent) {
          const current = memo[currentSpanId];
          memo[currentSpanId] = current.concat(span.textContent);
        } else {
          currentSpanId = undefined;
        }
        prevTop = top;
        return memo;
      }, {} as { [key: string]: string });

      setTextBreaks(texts);
    }
  }, []);

  const updateSvgTextPaths = (dimensions: Dimensions) => {
    const svg = svgRef.current;
    if (!svg) return;

    const { width, padding, height } = dimensions;
    const pixelRadius = radius * width;
    const cx = width / 2;
    const cy = pixelRadius + padding;
    const x1 = cx - pixelRadius;
    const x2 = cx + pixelRadius;
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

    const svgPathElements: SvgPathElement[] = Object.entries(textBreaks).map(
      ([id, text], index) => {
        const pathY = cy + index * padding;
        const pathD = `M ${x1},${pathY} A ${pixelRadius},${pixelRadius} 0 0,1 ${x2},${pathY}`;
        return { id, text, svgPath: pathD };
      }
    );

    setSvgPathElements(svgPathElements);
  };

  const updateDimensions = useCallback(() => {
    const original = originalTextRef.current;
    const textEl = textRef.current;
    if (!original || !textEl) return;

    const bbox = textEl.getBBox();
    const fontSize = parseFloat(getComputedStyle(original).fontSize || '16');
    setDimensions({
      width: original.offsetWidth,
      height: bbox.height,
      padding: fontSize,
    });
  }, []);

  useEffect(() => {
    updateSvgTextPaths(dimensions);
  }, [radius, dimensions, JSON.stringify(Object.keys(textBreaks))]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      updateDimensions();
      updateTextBreaks();
    }, 0);
    return () => clearTimeout(timeout);
  }, [
    parseInt(`${originalTextRef.current?.offsetWidth}`),
    parseInt(`${textRef.current?.getBBox().height}`),
    JSON.stringify(Object.keys(textBreaks)),
  ]);

  useLayoutEffect(() => {
    updateDimensions();
    updateTextBreaks();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  return (
    <div>
      <div ref={originalTextRef} className={`${className} tw-h-0 tw-text-transparent`}>
        {text}
      </div>
      <svg
        width={dimensions.width}
        height={dimensions.height}
        ref={svgRef}
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {svgPathElements.map((item) => (
            <path key={item.id} id={item.id} d={item.svgPath} fill="none" />
          ))}
        </defs>
        <text ref={textRef} textAnchor="middle" fill="currentColor" className={className}>
          {svgPathElements.map((item) => (
            <textPath key={item.id} href={`#${item.id}`} startOffset="50%" textAnchor="middle">
              {item.text}
            </textPath>
          ))}
        </text>
      </svg>
    </div>
  );
};

export const CurveDiv: React.FC<{
  scale?: number;
  className?: string;
}> = ({ scale = 0, className = '' }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const curvePathRef = useRef<SVGPathElement>(null);

  useEffect(() => {
    const update = () => {
      if (svgRef.current && curvePathRef.current) {
        svgRef.current.style.top = `-${svgRef.current.clientHeight - 3}px`;
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div className={`${className} tw-relative tw-border-t-2 tw-border-solid`}>
      <svg
        ref={svgRef}
        width="1517"
        height="93"
        viewBox="0 0 1517 93"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          transform: `translate3d(0px, 0px, 0px) scale(1, ${scale})`,
          position: 'absolute',
          width: '100%',
          height: 'auto',
          left: 0,
          transformOrigin: '0 100%',
        }}
      >
        <path
          ref={curvePathRef}
          d="M0 76C528.5 -28.9327 977.5 -28.9327 1600 92.0674H0Z"
        ></path>
      </svg>
    </div>
  );
};
